import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Typography,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@mui/material";
import { get, put } from "src/services/httpClient.service";
import { useSnackbar } from "src/components/snackbar/useSnackbar";

/* =========================
   Types
========================= */
interface Vendor {
  vendorId: string;
  vendorName: string;
}

interface UpdateZoneProps {
  handleClose: () => void;
  Zoneidvalues: any;
  fetchZones: () => void;
  selectedVendorId: string; // Add this prop
}

const BoardUpdateZone: React.FC<UpdateZoneProps> = ({
  handleClose,
  Zoneidvalues,
  fetchZones,
  selectedVendorId // Receive the selected vendor ID
}) => {
  const APIURL = process.env.REACT_APP_BASE_API_URL;
  const { showSnackbar } = useSnackbar();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    id: 0,
    vendorId: selectedVendorId || "", // Initialize with passed vendor ID
    name: "",
    isActive: true
  });

  /* =========================
     Fetch Vendors (for display purposes only)
  ========================= */
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await get(`${APIURL}/api/vendors`);
        setVendors(res?.data || res);
      } catch {
        showSnackbar("Failed to load vendors", "error");
      }
    };

    fetchVendors();
  }, [APIURL, showSnackbar]);

  /* =========================
     Prefill Zone Data
  ========================= */
  useEffect(() => {
    if (Zoneidvalues) {
      setForm({
        id: Zoneidvalues.id,
        vendorId: selectedVendorId || Zoneidvalues.vendorId || "", // Use selectedVendorId if available
        name: Zoneidvalues.name || "",
        isActive: Zoneidvalues.isActive
      });
    }
  }, [Zoneidvalues, selectedVendorId]);

  /* =========================
     Handlers
  ========================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!form.vendorId || !form.name) {
      showSnackbar("Vendor and Zone Name are required", "warning");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        id: form.id,
        vendorId: form.vendorId,
        name: form.name,
        isActive: form.isActive
      };

      const response = await put(
        `${APIURL}/api/miscellaneous/zone`,
        payload
      );

      if (response?.statusCode === 200) {
        showSnackbar(
          response?.message || "Zone updated successfully",
          "success"
        );
        fetchZones();
        handleClose();
      } else {
        showSnackbar(
          response?.message || "Failed to update zone",
          "error"
        );
      }
    } catch (error: any) {
      showSnackbar(error?.message || "Failed to update zone", "error");
    } finally {
      setLoading(false);
    }
  };

  // Find the selected vendor name for display
  const selectedVendor = vendors.find(v => v.vendorId === form.vendorId);

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Update Zone
      </Typography>

      <Grid container spacing={2}>
        {/* Vendor Display - Read Only */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="vendor-label">Vendor</InputLabel>
            <Select
              labelId="vendor-label"
              label="Vendor"
              value={form.vendorId || ""}
              disabled
              inputProps={{ readOnly: true }}
            >
              <MenuItem value={form.vendorId || ""}>
                {selectedVendor?.vendorName || form.vendorId || "Select Vendor"}
              </MenuItem>
            </Select>
            <FormHelperText>Vendor is selected from the parent list</FormHelperText>
          </FormControl>
          <input type="hidden" name="vendorId" value={form.vendorId} />
          <input type="hidden" name="id" value={form.id} />
        </Grid>

        {/* Zone Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Zone Name"
            name="name"
            value={form.name}
            onChange={handleChange}
          />
        </Grid>

        {/* Active Switch */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={e =>
                  setForm(prev => ({
                    ...prev,
                    isActive: e.target.checked
                  }))
                }
              />
            }
            label="Is Active"
          />
        </Grid>

        {/* Actions */}
        <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Update
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BoardUpdateZone;